import { isSameDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import type { DateRange, DayPickerProps } from 'react-day-picker';
import { formatShortDate } from '../../lib/date-helpers.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import type { CustomDateTimeRange } from '../../server/utils/explorer-utils.ts';
import { VisibilityWindow, dateRangeLookup, getDateRange } from '../../server/utils/explorer-utils.ts';
import { ButtonRadioVerticalList, ButtonRadioVerticalListItem } from './button-radio-vertical-list.tsx';
import { Button } from './button.tsx';
import { Calendar } from './calendar.tsx';
import { ResponsivePopover } from './responsive-popover.tsx';
import { SelectTriggerButton } from './select-trigger-button.tsx';

type WrapperProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
} & React.PropsWithChildren;

export const PresetDatePickerWrapper: React.FC<WrapperProps> = ({ children, open, setOpen }) => {
    return (
        <ResponsivePopover open={open} onOpenChange={setOpen}>
            <ResponsivePopover.Anchor asChild>
                <div className="flex">{children}</div>
            </ResponsivePopover.Anchor>
        </ResponsivePopover>
    );
};

export type VisibilityConfig = {
    preset: VisibilityWindow;
    dateRange: DateRange | null;
};

// NOTE - the difference here is DateRange requires both from and to fields. Cannot be submitted without both values
export type SubmittedVisibilityConfig = {
    preset: VisibilityWindow;
    dateRange: CustomDateTimeRange | null;
};

export type DatePickerProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    value: VisibilityConfig;
    onChange: (newValues: SubmittedVisibilityConfig) => void;
} & DayPickerProps;

export const PresetDatePicker: React.FC<DatePickerProps> = ({
    open,
    value,
    onChange,
    setOpen,
}: DatePickerProps) => {
    const { t, locale } = useLocale();
    const [selected, setSelected] = React.useState<VisibilityConfig>({
        preset: value.preset,
        dateRange:
            value.preset === VisibilityWindow.CUSTOM_DATES
                ? (value.dateRange ?? null)
                : getDateRange(value.preset),
    });

    const selectedPreset = selected.preset;
    const selectedDateRange = selected.dateRange;

    const submitIsDisabled = !selectedDateRange?.from || !selectedDateRange.to;

    const getDisplayText = (config: VisibilityConfig): string => {
        const { preset, dateRange } = config;

        switch (preset) {
            case VisibilityWindow.TODAY:
                return t('option__today', { ns: 'preset-date-picker' });
            case VisibilityWindow.YESTERDAY:
                return t('option__yesterday', { ns: 'preset-date-picker' });
            case VisibilityWindow.LAST_WEEK:
                return t('option__last_week', { ns: 'preset-date-picker' });
            case VisibilityWindow.LAST_MONTH:
                return t('option__last_month', { ns: 'preset-date-picker' });
            case VisibilityWindow.LAST_QUARTER:
                return t('option__last_quarter', { ns: 'preset-date-picker' });
            case VisibilityWindow.TTM:
                return t('option__ttm', { ns: 'preset-date-picker' });
            case VisibilityWindow.FULL_YEAR:
                return t('option__full_year', { ns: 'preset-date-picker' });
            case VisibilityWindow.YTD:
                return t('option__ytd', { ns: 'preset-date-picker' });
            case VisibilityWindow.CUSTOM_DATES:
                return dateRange?.from
                    ? `${formatShortDate(dateRange.from, locale)} – ${formatShortDate(dateRange.to, locale)}`
                    : t('option__custom', { ns: 'preset-date-picker' });
            default:
                return t('option__custom', { ns: 'preset-date-picker' });
        }
    };

    const setDateRange = (range: DateRange | null) => {
        const matchedPreset = matchPreset(range);
        setSelected({
            preset: matchedPreset,
            dateRange: range,
        });
    };

    const setSelectedPreset = (preset: VisibilityWindow) => {
        setSelected({
            preset,
            dateRange: getDateRange(preset),
        });
    };

    const matchPreset = (range: DateRange | null) => {
        if (!range?.from || !range.to) {
            return VisibilityWindow.CUSTOM_DATES;
        }

        for (const preset in VisibilityWindow) {
            const presetKey = preset as keyof typeof VisibilityWindow;
            if (presetKey === VisibilityWindow.CUSTOM_DATES) {
                continue;
            }

            const { from, to } = dateRangeLookup[VisibilityWindow[presetKey]]();

            if (isSameDay(from, range.from) && isSameDay(to, range.to)) {
                return VisibilityWindow[presetKey];
            }
        }

        return VisibilityWindow.CUSTOM_DATES;
    };

    const onCancel = () => {
        setOpen(false);
    };

    const onSubmit = () => {
        // Make sure there is from and to. On submit should not be possible without both values being non-null
        if (submitIsDisabled) {
            return;
        }
        onChange({
            preset: selectedPreset,
            dateRange:
                selectedPreset === VisibilityWindow.CUSTOM_DATES &&
                selectedDateRange.from &&
                selectedDateRange.to
                    ? {
                          from: selectedDateRange.from,
                          to: selectedDateRange.to,
                      }
                    : null,
        });
        setOpen(false);
    };

    return (
        <>
            <ResponsivePopover.Trigger asChild onOpenChange={setOpen}>
                <SelectTriggerButton>
                    <div className="flex gap-2 items-center">
                        <CalendarIcon className="h-4 w-4" />
                        <span className="text-sm text-neutral-900 ">{getDisplayText(value)}</span>
                    </div>
                </SelectTriggerButton>
            </ResponsivePopover.Trigger>
            <ResponsivePopover.Content
                open={open}
                onOpenChange={setOpen}
                align="start"
                className="w-auto p-0 sm:max-h-(--radix-popover-content-available-height) overflow-auto"
            >
                <div className="flex flex-col grow h-dvh sm:h-full">
                    <div className="flex grow">
                        <div className="flex-col p-3 hidden sm:flex">
                            <div className="w-40">
                                <ButtonRadioVerticalList
                                    value={selectedPreset}
                                    onValueChange={setSelectedPreset}
                                >
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.TODAY}>
                                        {t('option__today', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.LAST_WEEK}>
                                        {t('option__last_week', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.LAST_MONTH}>
                                        {t('option__last_month', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.LAST_QUARTER}>
                                        {t('option__last_quarter', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.TTM}>
                                        {t('option__ttm', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.FULL_YEAR}>
                                        {t('option__full_year', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.YTD}>
                                        {t('option__ytd', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                    <ButtonRadioVerticalListItem value={VisibilityWindow.CUSTOM_DATES}>
                                        {t('option__custom', { ns: 'preset-date-picker' })}
                                    </ButtonRadioVerticalListItem>
                                </ButtonRadioVerticalList>
                            </div>
                        </div>
                        <div className="flex flex-col items-center gap-y-2 grow">
                            <Calendar
                                mode="range"
                                numberOfMonths={2}
                                selected={selectedDateRange ?? undefined}
                                onSelect={(range: any) => {
                                    setDateRange(range ?? null);
                                }}
                                classNames={{
                                    months: 'flex flex-col grow space-y-0 relative sm:flex-row',
                                    month: 'space-y-4 p-3 w-full',
                                    month_grid: 'rdp-month_grid w-full',
                                }}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div
                        className={cn(
                            'flex flex-col sm:flex-row items-center justify-end gap-3 border-t border-neutral-200 px-3 py-3',
                        )}
                    >
                        <div className="flex grow">
                            <p className="rounded-sm bg-gray-100 p-2 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                                {t('selected_range', { ns: 'preset-date-picker' })}:{' '}
                                {selectedDateRange?.from
                                    ? `${formatShortDate(selectedDateRange.from, locale)} – ${formatShortDate(selectedDateRange.to, locale)}`
                                    : t('selected_range_not_selected', { ns: 'preset-date-picker' })}
                            </p>
                        </div>
                        <div className="flex w-full gap-3 justify-end">
                            <Button variant="subtle" onClick={onCancel}>
                                {t('form__cancel')}
                            </Button>
                            <Button variant="primary" disabled={submitIsDisabled} onClick={onSubmit}>
                                {`${t('apply')} `}
                            </Button>
                        </div>
                    </div>
                </div>
            </ResponsivePopover.Content>
        </>
    );
};
